import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRightLeft, Edit, Loader2, Trash2 } from 'lucide-react';
import { travelCariSourceSchema } from '@polatkan/api/travel-cari';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { TravelCariAccountEditor } from '@/components/travel/cari/account-editor';
import { TravelCariAccountAssignment } from '@/components/travel/cari/account-assignment';
import {
  TravelCariPartnerOperations,
  TravelCariCustomerBookings,
} from '@/components/travel/cari/account-operations';
import { MoveCariTypeDialog } from '@/components/travel/cari/move-cari-type-dialog';
import { accountErrorKey } from '@/components/travel/cari/account-form-model';
import { cariTypeLabel } from '@/components/travel/cari/cari-type-display';
import { BankAccountsTab } from '@/components/travel/shared/bank-accounts-tab';
import { DocumentsCard } from '@/components/travel/customers/documents-card';
import { TravelCustomerAnalyticsTab } from '@/components/travel/customers/analytics-tab';
import { TravelCariLedgerPage } from './movements';

export function TravelCariAccountPage() {
  const { source: sourceParam, id } = useParams();
  const parsed = travelCariSourceSchema.safeParse(sourceParam);
  const source = parsed.success ? parsed.data : 'partner';
  const { t, i18n } = useTranslation('travelCari');
  const { t: common } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const backTo =
    typeof returnTo === 'string' && /^\/travel\/cari(?:\?|$)/.test(returnTo)
      ? returnTo
      : '/travel/cari';
  const [params, setParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const {
    data: account,
    isLoading,
    error,
    refetch,
  } = trpc.travelCari.accounts.byId.useQuery(
    { source, id: id ?? '' },
    { enabled: parsed.success && !!id }
  );
  const utils = trpc.useUtils();
  const changeStatus = trpc.travelCari.accounts.setStatus.useMutation({
    onSuccess: () => {
      setStatusError(null);
      void utils.travelCari.invalidate();
    },
    onError: (error) => setStatusError(t(`account.errors.${accountErrorKey(error)}`)),
  });
  const remove = trpc.travelCariType.deleteCari.useMutation({
    onSuccess: () => {
      void utils.travelCari.invalidate();
      void utils.travelCariType.invalidate();
      navigate('/travel/cari');
    },
    onError: (error) => {
      setDeleteError(
        t(
          `account.errors.${error.message === 'DEV_PREVIEW_READ_ONLY' ? 'readOnly' : error.data?.code === 'PRECONDITION_FAILED' ? 'hasHistory' : accountErrorKey(error)}`
        )
      );
    },
  });
  const back = (
    <Link
      to={backTo}
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('account.back')}
    </Link>
  );
  if (isLoading && parsed.success)
    return (
      <div className="space-y-5">
        {back}
        <p className="flex items-center gap-2" role="status">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('account.loading')}
        </p>
      </div>
    );
  if (!parsed.success || error || !account)
    return (
      <div className="space-y-5">
        {back}
        <div role="alert" className="space-y-3 border p-5">
          <p>{t('account.loadFailed')}</p>
          <Button
            variant="outline"
            onClick={() => {
              void refetch();
            }}
          >
            {common('retry', 'Retry')}
          </Button>
        </div>
      </div>
    );
  const label = account.cariType
    ? cariTypeLabel(account.cariType, i18n.language)
    : account.cariTypeCode;
  const details = account.fields;
  const organisation = 'customerType' in account && account.customerType !== 'INDIVIDUAL';
  const commercial = 'partnerType' in account && account.partnerType !== 'VENDOR';
  const tab = params.get('tab') ?? 'overview';
  const validTabs = [
    'overview',
    ...(account.permissions.ledger ? ['ledger'] : []),
    ...(account.permissions.operations
      ? ['operations', 'bank', ...(source === 'customer' ? ['documents', 'analytics'] : [])]
      : []),
  ];
  const selectTab = (tab: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (tab === 'overview') next.delete('tab');
      else next.set('tab', tab);
      return next;
    });
  const item = (name: keyof typeof details) => (
    <div key={name}>
      <dt className="text-muted-foreground text-xs">{t(`account.fields.${name}`)}</dt>
      <dd className="mt-1 break-words text-sm">
        {String(details[name] || t('account.notProvided'))}
      </dd>
    </div>
  );
  return (
    <div className="space-y-5">
      {back}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{account.name}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {account.cariTypeCode} · {label}
            {account.legacyCariCode && ` · ${account.legacyCariCode}`}
          </p>
          {'status' in account && (
            <p className="text-muted-foreground mt-1 text-xs">
              {t(`account.status.${account.status}`)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {account.permissions.status && 'status' in account && (
            <select
              aria-label={t('directory.status')}
              className="bg-background h-8 border px-2 text-sm"
              value={account.status}
              disabled={changeStatus.isPending}
              onChange={(event) =>
                changeStatus.mutate({
                  id: account.id,
                  expectedUpdatedAt: account.updatedAt,
                  status: event.target.value as
                    | 'PENDING_APPROVAL'
                    | 'ACTIVE'
                    | 'INACTIVE'
                    | 'BLACKLISTED',
                })
              }
            >
              {['PENDING_APPROVAL', 'ACTIVE', 'INACTIVE', 'BLACKLISTED'].map((status) => (
                <option key={status} value={status}>
                  {t(`account.status.${status}`)}
                </option>
              ))}
            </select>
          )}
          {account.permissions.accountingEdit && (
            <Button variant="outline" onClick={() => setMoveOpen(true)}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              {t('cariTypes.moveCari')}
            </Button>
          )}
          {account.permissions.edit && (
            <Button onClick={() => setEditOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              {t('account.edit')}
            </Button>
          )}
        </div>
      </header>
      {statusError && (
        <p role="alert" className="text-destructive text-sm">
          {statusError}
        </p>
      )}
      <Tabs value={validTabs.includes(tab) ? tab : 'overview'} onValueChange={selectTab}>
        <TabsList className="max-w-full overflow-x-auto">
          {validTabs.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(
                value === 'operations' && source === 'partner' && !commercial
                  ? 'account.tabs.contacts'
                  : `account.tabs.${value}`
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            <section className="bg-card space-y-4 border p-5">
              <h2 className="font-semibold">{t('account.contact')}</h2>
              <dl className="grid gap-4 sm:grid-cols-2">
                {source === 'partner' && item('contactName')}
                {organisation && (
                  <>
                    {item('firstName')}
                    {item('lastName')}
                  </>
                )}
                {item('email')}
                {item('phone')}
                {source === 'customer' && item('secondaryPhone')}
                {source === 'partner' && (
                  <div>
                    <dt className="text-muted-foreground text-xs">{t('account.fields.website')}</dt>
                    <dd className="mt-1 text-sm">
                      {/^https?:\/\//i.test(details.website) ? (
                        <a
                          href={details.website}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          {details.website}
                        </a>
                      ) : (
                        details.website || t('account.notProvided')
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </section>
            <section className="bg-card space-y-4 border p-5">
              <h2 className="font-semibold">{t('account.address')}</h2>
              <dl className="grid gap-4 sm:grid-cols-2">
                {item('addressLine1')}
                {item('city')}
                {item('country')}
              </dl>
            </section>
            <section className="bg-card space-y-4 border p-5">
              <h2 className="font-semibold">{t('account.finance')}</h2>
              <dl className="grid gap-4 sm:grid-cols-2">
                {item('currency')}
                <div>
                  <dt className="text-muted-foreground text-xs">
                    {t('account.fields.creditLimit')}
                  </dt>
                  <dd className="mt-1 text-sm">
                    {new Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: details.currency,
                    }).format(details.creditLimit)}
                  </dd>
                </div>
                {commercial ? (
                  <>
                    <div>
                      <dt className="text-muted-foreground text-xs">
                        {t('account.fields.paymentTerms')}
                      </dt>
                      <dd className="mt-1 text-sm">{t(`account.terms.${details.paymentTerms}`)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">
                        {t('account.fields.commissionRate')}
                      </dt>
                      <dd className="mt-1 text-sm">{details.commissionRate}%</dd>
                    </div>
                  </>
                ) : (
                  source === 'customer' && (
                    <div>
                      <dt className="text-muted-foreground text-xs">
                        {t('account.fields.creditLimitEnabled')}
                      </dt>
                      <dd className="mt-1 text-sm">
                        {common(details.creditLimitEnabled ? 'yes' : 'no')}
                      </dd>
                    </div>
                  )
                )}
              </dl>
            </section>
            <section className="bg-card space-y-4 border p-5">
              <h2 className="font-semibold">{t('account.identity')}</h2>
              <dl className="grid gap-4 sm:grid-cols-2">
                {source === 'partner' || organisation ? (
                  <>
                    {item('companyName')}
                    {item('tradeName')}
                    {item('taxId')}
                    {item('registrationNo')}
                  </>
                ) : (
                  <>
                    {item('firstName')}
                    {item('lastName')}
                  </>
                )}
                <div>
                  <dt className="text-muted-foreground text-xs">{t('account.updatedAt')}</dt>
                  <dd className="mt-1 text-sm">{new Date(account.updatedAt).toLocaleString()}</dd>
                </div>
              </dl>
            </section>
          </div>
          {details.notes && (
            <section className="bg-card space-y-3 border p-5">
              <h2 className="font-semibold">{t('account.fields.notes')}</h2>
              <p className="whitespace-pre-wrap text-sm">{details.notes}</p>
            </section>
          )}
          {account.permissions.accountingView && (
            <TravelCariAccountAssignment
              source={source}
              id={account.id}
              typeCode={account.cariTypeCode}
              currentAccountId={account.muhasebeAccountId}
              canEdit={account.permissions.accountingEdit}
            />
          )}
          {account.permissions.delete && (
            <div className="flex flex-wrap items-center justify-between gap-3 border p-5">
              <p className="text-muted-foreground text-sm">{t('account.deleteHint')}</p>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteError(null);
                  setDeleteOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {common('delete')}
              </Button>
            </div>
          )}
          {deleteError && (
            <p role="alert" className="text-destructive text-sm">
              {deleteError}
            </p>
          )}
        </TabsContent>
        <TabsContent value="ledger" className="mt-5">
          {tab === 'ledger' && account.permissions.ledger && (
            <TravelCariLedgerPage
              account={{
                source,
                id: account.id,
                name: account.name,
                legacyCariCode: account.legacyCariCode,
                canExport: account.permissions.statementExport,
              }}
            />
          )}
        </TabsContent>
        {account.permissions.operations && (
          <>
            <TabsContent value="operations" className="mt-5">
              {tab === 'operations' &&
                (source === 'partner' ? (
                  <TravelCariPartnerOperations
                    id={account.id}
                    canEdit={account.permissions.edit}
                    commercial={commercial}
                  />
                ) : (
                  <TravelCariCustomerBookings id={account.id} />
                ))}
            </TabsContent>
            <TabsContent value="bank" className="mt-5">
              {tab === 'bank' && (
                <BankAccountsTab
                  entityType={source}
                  entityId={account.id}
                  canEdit={account.permissions.edit}
                />
              )}
            </TabsContent>
            {source === 'customer' && (
              <>
                <TabsContent value="documents" className="mt-5">
                  {tab === 'documents' && (
                    <DocumentsCard customerId={account.id} canEdit={account.permissions.edit} />
                  )}
                </TabsContent>
                <TabsContent value="analytics" className="mt-5">
                  {tab === 'analytics' && <TravelCustomerAnalyticsTab customerId={account.id} />}
                </TabsContent>
              </>
            )}
          </>
        )}
      </Tabs>
      {editOpen && (
        <TravelCariAccountEditor
          open
          onOpenChange={setEditOpen}
          account={account}
          type={{
            source,
            code: account.cariTypeCode,
            label,
            organisation,
            serviceKind: 'partnerType' in account ? account.partnerType : undefined,
          }}
          onSaved={() => {
            void refetch();
          }}
        />
      )}
      <MoveCariTypeDialog
        cari={moveOpen ? { id: account.id, companyName: account.name, source } : null}
        currentTypeId={account.cariTypeId ?? account.cariType?.id ?? ''}
        onOpenChange={setMoveOpen}
        onMoved={() => {
          void utils.travelCari.invalidate();
          void refetch();
        }}
      />
      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={common('delete')}
        description={t('account.deleteConfirm', { name: account.name })}
        error={deleteError}
        variant="destructive"
        confirmLabel={common('delete')}
        isLoading={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync({ source, id: account.id });
        }}
      />
    </div>
  );
}
